import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useCallback } from "react";

import type { FluxoPendenteDoUsuarioType } from "@repo/graphql-types/graphql";

import { StartFlow } from "@/components/flows/start-flow";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { getActiveSteps, isStepSkippable, useFlowData } from "@/hooks/use-flow-data";

const FlowsPage = (): JSX.Element => {
  const navigate = useNavigate();

  const { data, isLoading } = useFlowData();

  const flows = (data?.membro?.fluxosPendentes ?? []).filter(
    (f): f is FluxoPendenteDoUsuarioType => f !== null,
  );

  const currentFlow: FluxoPendenteDoUsuarioType | undefined = flows[0];

  const handleNext = useCallback((): void => {
    if (!currentFlow) {
      return;
    }

    const steps = getActiveSteps(currentFlow);
    const firstActiveStep = steps.find((s) => !isStepSkippable(s));

    if (!firstActiveStep) {
      void navigate({
        to: "/flows/complete/$flowId",
        params: { flowId: String(currentFlow.codFluxo) },
        replace: true,
      });
      return;
    }

    void navigate({
      to: "/flows/$stepId/$subStepId",
      params: {
        stepId: String(firstActiveStep.codEtapa),
        subStepId: "main",
      },
    });
  }, [currentFlow, navigate]);

  if (isLoading || !currentFlow) {
    return (
      <RoundedLayout>
        <FlowHeader>
          <FlowHeader.Actions>
            <FlowHeader.HelpButton />
          </FlowHeader.Actions>
          <FlowHeader.ProgressBar progress={0} />
        </FlowHeader>
        <FullPageSpinner />
      </RoundedLayout>
    );
  }

  return <StartFlow flow={currentFlow} nextStep={handleNext} />;
};

export const Route = createFileRoute("/flows/")({
  component: FlowsPage,
});
